import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { Settings } from '../../_core/settings';
import { Core } from '../../_core/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ToastService } from '../../_core/toast.service';
import { HomeS } from '../../_core/home';

@Component({
  selector: 'app-settlement-emails',
  imports: [
    ButtonModule,
    CardModule,
    DialogModule,
    IconFieldModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputIconModule,
    InputTextModule,
    FloatLabelModule,
    TableModule,
    ReactiveFormsModule,
  ],
  templateUrl: './settlement-emails.html',
  styleUrl: './settlement-emails.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettlementEmails implements OnInit {
  public $settings: Settings = inject(Settings);
  private $core: Core = inject(Core);
  private $toast = inject(ToastService);
  private $home = inject(HomeS);

  public readonly statusCardMode = computed(() => {
    if (!this.$settings.hasElectronicSettlement()) {
      return 'inactive' as const;
    }

    if (this.$settings.hasMailboxDeliveryService()) {
      return 'activeWithMailbox' as const;
    }

    return 'active' as const;
  });

  public emailCtrl: FormControl = new FormControl('', Validators.email);
  public showDeactivateDialog = false;
  public showMailboxOnActivateDialog = false;
  public showMailboxDeactivateDialog = false;

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  private get tenantId(): string {
    return this.$core.selectedBuilding()!.tenantId;
  }

  public get isActive(): boolean {
    return this.$settings.hasElectronicSettlement();
  }

  public async loadData(): Promise<void> {
    await this.$settings.fetchSettlementEmails(this.tenantId);
  }

  public addMail(): void {
    if (this.emailCtrl.invalid || !this.emailCtrl.value) {
      this.emailCtrl.markAsTouched();
      return;
    }

    this.$settings.settlementEmails.update((list) => [...list, this.emailCtrl.value]);
    this.emailCtrl.reset();
  }

  public deleteMail(mail: string): void {
    this.$settings.settlementEmails.update((list) => list.filter((em) => em !== mail));
  }

  public async submit(): Promise<void> {
    if (this.$settings.settlementEmails().length === 0) {
      return;
    }

    if (!this.isActive && this.$settings.hasMailboxDeliveryService()) {
      this.showMailboxOnActivateDialog = true;
      return;
    }

    await this.performActivate(false);
  }

  public async confirmActivateWithMailboxChoice(disableMailbox: boolean): Promise<void> {
    this.showMailboxOnActivateDialog = false;
    await this.performActivate(disableMailbox);
  }

  private async performActivate(disableMailboxDeliveryService: boolean): Promise<void> {
    const response = await this.$settings.activateEmailDeliveryService(this.tenantId, {
      emails: this.$settings.settlementEmails(),
      disableMailboxDeliveryService,
    });

    if (response) {
      await this.refreshAfterChange();
      this.$toast.success('Cambios guardados con éxito');
    } else {
      this.$toast.error('Hubo un problema al procesar los datos');
    }
  }

  public openDeactivateDialog(): void {
    this.showDeactivateDialog = true;
  }

  public async confirmDeactivate(): Promise<void> {
    this.showDeactivateDialog = false;
    const response = await this.$settings.deactivateEmailDeliveryService(this.tenantId);

    if (response) {
      await this.refreshAfterChange();
      this.$toast.success('ReciboVerde desactivado');
    } else {
      this.$toast.error('No se pudo desactivar ReciboVerde');
    }
  }

  public openMailboxDeactivateDialog(): void {
    this.showMailboxDeactivateDialog = true;
  }

  public async confirmMailboxDeactivate(): Promise<void> {
    this.showMailboxDeactivateDialog = false;
    const response = await this.$settings.deactivateMailboxDeliveryService(this.tenantId);

    if (response) {
      await this.loadData();
      this.$toast.success('Buzoneo desactivado');
    } else {
      this.$toast.error('No se pudo desactivar el buzoneo');
    }
  }

  private async refreshAfterChange(): Promise<void> {
    await this.loadData();
    await this.$home.fetchHomeData(true);
  }
}
